import { useEffect, useMemo, useState } from 'react';
import { useNotepad } from '../contexts/NotepadContext';
import { visibilityMeta } from '../domain/notepad';

/**
 * Manage a notebook's sharing scope (author only).
 *
 *   Team    → every member on the case (the default)
 *   Shared  → you + a chosen set of members (view or edit)
 *   Private → only you
 *
 * "Shared" reveals a member picker; saving writes both the visibility and the
 * grant list. Backend: PATCH /notebooks/{id} (visibility) +
 * PUT /notebooks/{id}/shares. Mirrors the per-notebook model in
 * plans/NOTEPAD_SHARING_PLAN.md.
 */
export default function ShareNotebookModal({ notebook, onClose }) {
  const {
    loadOrgMembers, getNotebookShares, setNotebookShares,
    setNotebookVisibility, currentUserId,
  } = useNotepad();

  const [visibility, setVisibility] = useState(notebook.visibility || 'organization');
  const [members, setMembers] = useState([]);
  // Map user_id -> permission ('view' | 'edit') for currently-granted members.
  const [grants, setGrants] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [mem, shares] = await Promise.all([
        loadOrgMembers(),
        getNotebookShares(notebook.id),
      ]);
      if (!alive) return;
      setMembers((mem || []).filter(m => m.user_id !== notebook.author_id));
      const g = {};
      (shares?.shares || []).forEach(s => { g[s.user_id] = s.permission; });
      setGrants(g);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [notebook.id, notebook.author_id, loadOrgMembers, getNotebookShares]);

  const toggleMember = (uid) => setGrants(prev => {
    const next = { ...prev };
    if (next[uid]) delete next[uid];
    else next[uid] = 'view';
    return next;
  });
  const setPermission = (uid, permission) => setGrants(prev => ({ ...prev, [uid]: permission }));

  const grantCount = useMemo(() => Object.keys(grants).length, [grants]);

  const save = async () => {
    setSaving(true);
    try {
      if (visibility === 'shared') {
        // Writing shares flips visibility to 'shared' server-side.
        const shares = Object.entries(grants).map(([user_id, permission]) => ({ user_id, permission }));
        await setNotebookShares(notebook.id, shares);
      } else {
        // Private / Team: clear any grants, then set the scope.
        await setNotebookShares(notebook.id, []);
        await setNotebookVisibility(notebook.id, visibility);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // Only the author manages sharing.
  const isAuthor = !!currentUserId && notebook.author_id === currentUserId;

  return (
    <div className="np-export-overlay" onClick={onClose}>
      <div className="np-export-modal np-share-modal" onClick={e => e.stopPropagation()}>
        <div className="np-export-head">
          <h3>Share “{notebook.name}”</h3>
          <button className="np-icon-btn" onClick={onClose} title="Close">✕</button>
        </div>

        {!isAuthor ? (
          <div className="np-share-body">
            <p className="np-empty">Only the notebook’s owner can change sharing.</p>
          </div>
        ) : (
          <>
            <div className="np-share-body">
              <div className="np-share-scopes">
                {['organization', 'shared', 'private'].map(v => {
                  const meta = visibilityMeta(v);
                  return (
                    <label key={v} className={`np-share-scope ${visibility === v ? 'on' : ''}`}>
                      <input
                        type="radio"
                        name="np-visibility"
                        checked={visibility === v}
                        onChange={() => setVisibility(v)}
                      />
                      <span className="np-share-scope-main">
                        <span className="np-share-scope-title">{meta.icon} {meta.label}</span>
                        <span className="np-share-scope-desc">{meta.desc}</span>
                      </span>
                    </label>
                  );
                })}
              </div>

              {visibility === 'shared' && (
                <div className="np-share-members">
                  <div className="np-menu-label">
                    Members {grantCount > 0 ? `(${grantCount})` : ''}
                  </div>
                  {loading && <div className="np-empty">Loading members…</div>}
                  {!loading && members.length === 0 && (
                    <div className="np-empty">No other members in your organization.</div>
                  )}
                  {!loading && members.map(m => {
                    const on = !!grants[m.user_id];
                    return (
                      <div key={m.user_id} className={`np-share-member ${on ? 'on' : ''}`}>
                        <label className="np-share-member-pick">
                          <input type="checkbox" checked={on} onChange={() => toggleMember(m.user_id)} />
                          <span className="np-share-member-name">
                            {m.display_name || m.email || m.user_id}
                            {m.email && m.display_name && (
                              <span className="np-share-member-email"> · {m.email}</span>
                            )}
                          </span>
                        </label>
                        {on && (
                          <select
                            className="np-share-perm"
                            value={grants[m.user_id]}
                            onChange={(e) => setPermission(m.user_id, e.target.value)}
                          >
                            <option value="view">Can view</option>
                            <option value="edit">Can edit</option>
                          </select>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="np-export-foot">
              <span style={{ flex: 1 }} />
              <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
              <button
                className="np-add-btn"
                onClick={save}
                disabled={saving || (visibility === 'shared' && grantCount === 0)}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
